import { useEffect, useState } from "react";

/**
 * Instradamento minimo.
 *
 * Le schermate sono poche: una libreria di routing porterebbe più concetti di
 * quanti ne servano. Qui il percorso è una funzione pura, quindi verificabile.
 */

export const SEZIONI_IMPOSTAZIONI = ["utenti", "accessi", "registry", "token", "email", "cartelle", "backup", "generale"] as const;
export type SezioneImpostazioni = (typeof SEZIONI_IMPOSTAZIONI)[number];

export type Route =
  | { name: "stacks" }
  | { name: "new-stack" }
  | { name: "import" }
  | { name: "stack"; stackId: number }
  | { name: "ports" }
  | { name: "audit" }
  | { name: "settings"; sezione: SezioneImpostazioni | null }
  | { name: "profilo" }
  | { name: "not-found" };

export function matchRoute(pathname: string): Route {
  const path = pathname.replace(/\/+$/, "") || "/";

  if (path === "/" || path === "/stacks") return { name: "stacks" };
  if (path === "/stacks/nuovo") return { name: "new-stack" };
  if (path === "/importazione") return { name: "import" };
  if (path === "/porte") return { name: "ports" };
  if (path === "/profilo") return { name: "profilo" };
  // Gli indirizzi vecchi (/registro, /utenti) restano validi: stanno nei segnalibri.
  if (path === "/utenti") return { name: "settings", sezione: "utenti" };
  if (path === "/attivita" || path === "/registro") return { name: "audit" };
  if (path === "/impostazioni") return { name: "settings", sezione: null };

  const sezione = /^\/impostazioni\/([^/]+)$/.exec(path)?.[1];
  if (sezione && (SEZIONI_IMPOSTAZIONI as readonly string[]).includes(sezione)) {
    return { name: "settings", sezione: sezione as SezioneImpostazioni };
  }

  const stackMatch = /^\/stacks\/(\d+)$/.exec(path);
  if (stackMatch) return { name: "stack", stackId: Number(stackMatch[1]) };

  return { name: "not-found" };
}

/** Naviga senza ricaricare la pagina. */
export function navigate(path: string): void {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => matchRoute(window.location.pathname));

  useEffect(() => {
    const update = () => setRoute(matchRoute(window.location.pathname));
    window.addEventListener("popstate", update);

    return () => window.removeEventListener("popstate", update);
  }, []);

  return route;
}

/** Link che naviga senza ricaricare, ma resta un vero collegamento. */
export function linkProps(path: string) {
  return {
    href: path,
    onClick: (event: React.MouseEvent) => {
      // Lascia al browser i click con modificatori: aprire in una nuova scheda
      // deve continuare a funzionare.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      event.preventDefault();
      navigate(path);
    },
  };
}
